import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import SectionCard from "./SectionCard";
import FormikTextField from "./FormikTextField";
import { updateTermsAndConditions } from "../../../api/ap_services";

const Step8Review = ({
  formData,
  setFieldValue,
  vendorRegistrationId,
  groupId,
  onRedirectToStep0,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [pendingAgreedIso, setPendingAgreedIso] = useState("");
  const isFinalOtpVerified =
    formData?.termsAgreedDateTime && formData?.cobcAgreedDateTime;
  const hasAgreedToTerms = Boolean(
    formData.agreeTerms === true || formData.agreeTerms === "true",
  );

  const handleCloseOtpDialog = () => {
    if (submitting) return;
    setOtpDialogOpen(false);
    setOtpValue("");
  };

  const handleSendFinalOtp = async () => {
    if (groupId?.isAdmin) return;
    if (!hasAgreedToTerms) {
      toast.error("Please agree to the terms and conditions before continuing.");
      return;
    }
    if (!vendorRegistrationId) {
      toast.error("Please save your registration before OTP verification.");
      return;
    }

    const agreedIso = new Date().toISOString();
    setPendingAgreedIso(agreedIso);
    try {
      setSubmitting(true);
      const { response } = await updateTermsAndConditions({
        vendorRegistrationId: Number(vendorRegistrationId),
        termsAgreedDateTime: agreedIso,
      });
      if (response?.responseCode === true) {
        toast.success(
          response?.message || "OTP has been sent to your registered e-mail.",
        );
        setOtpValue("");
        setOtpDialogOpen(true);
      } else {
        toast.error(response?.message || "Could not send OTP.");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Request failed. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyFinalOtp = async () => {
    const otp = String(otpValue || "").trim();
    if (!otp) {
      toast.error("Please enter OTP.");
      return;
    }
    try {
      setSubmitting(true);
      const agreedIso = pendingAgreedIso || new Date().toISOString();
      const { response } = await updateTermsAndConditions({
        vendorRegistrationId: Number(vendorRegistrationId),
        termsOtp: otp,
        termsAgreedDateTime: agreedIso,
        cobcAgreedDateTime: agreedIso,
        isEdit: "N",
        overallComments: formData.overallComments,
      });
      if (response?.responseCode === true) {
        toast.success("Final OTP verified successfully.");
        setFieldValue("termsAgreedDateTime", agreedIso);
        setFieldValue("cobcAgreedDateTime", agreedIso);
        setFieldValue("termsOtp", "");
        setFieldValue("cobcOtp", "");
        setOtpDialogOpen(false);
        setOtpValue("");

        if (onRedirectToStep0) {
          setTimeout(() => {
            onRedirectToStep0();
          }, 1000);
        }
      } else {
        toast.error(response?.message || "Invalid OTP or verification failed.");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Request failed. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatYesNo = (value) => {
    if (value === "Y") return "YES";
    if (value === "N") return "NO";
    return "-";
};

  return (
    <>
      <SectionCard title="Note:">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ pl: 0, m: 0 }}>
              <Typography component="li" sx={{ fontSize: "15px", mb: 1 }}>
                “Neither me nor any of my relatives are employees with Modenik
                as of today. In case if someone gets an employment I would
                volunteer to declare the same to the management on the date when
                such person is given the employment offer”
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </SectionCard>
      <SectionCard title="">
         <Grid item xs={12}>
      <Typography variant="body2" gutterBottom>
        PAN Already Exists:
        <span
          style={{
            color:
              formData.isPanNoAlreadyExists === "Y"
                ? "red"
                : "green",
            fontWeight: 600,
            marginLeft: 8,
          }}
        >
          {formatYesNo(formData.isPanNoAlreadyExists)}
        </span>
      </Typography>
 
      <Typography variant="body2" gutterBottom>
        GST Already Exists:
        <span
          style={{
            color:
              formData.isGstNoAlreadyExists === "Y"
                ? "red"
                : "green",
            fontWeight: 600,
            marginLeft: 8,
          }}
        >
          {formatYesNo(formData.isGstNoAlreadyExists)}
        </span>
      </Typography>
 
      <Typography variant="body2" gutterBottom>
        Bank Account No Already Exists:
        <span
          style={{
            color:
              formData.isAccNoAlreadyExists === "Y"
                ? "red"
                : "green",
            fontWeight: 600,
            marginLeft: 8,
          }}
        >
          {formatYesNo(formData.isAccNoAlreadyExists)}
        </span>
      </Typography>
    </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{mt:2}}>
            <FormikTextField
              multiline
              rows={4}
              label="Overall Comments and General Observations"
              name="overallComments"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              Please review your information before submitting:
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(
                    formData.agreeTerms === true ||
                      formData.agreeTerms === "true",
                  )}
                  onChange={(e) =>
                    setFieldValue("agreeTerms", e.target.checked)
                  }
                  name="agreeTerms"
                />
              }
              label="I agree to the terms and conditions of Modenik"
            />
          </Grid>
          {!groupId?.isAdmin && (
            <>
              {isFinalOtpVerified ? (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "15px", color: "success.main" }}>
                    OTP verified.
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="button"
                    disabled={submitting || !hasAgreedToTerms}
                    onClick={handleSendFinalOtp}
                  >
                    SEND FINAL OTP
                  </Button>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </SectionCard>

      <Dialog
        open={otpDialogOpen}
        onClose={handleCloseOtpDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Final OTP</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            size="small"
            label="OTP"
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value)}
            disabled={submitting}
            inputProps={{
              inputMode: "numeric",
              autoComplete: "one-time-code",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOtpDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleVerifyFinalOtp}
            variant="contained"
            disabled={submitting}
          >
            Verify OTP
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Step8Review;
